import { computed, ref, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useConfigStore } from "@/store/config";
import { useRBCSlideStore } from "@/store/modules/RBCregion";
import type { RBCBackendPayload, ReportReviewPostPayload } from "@/types/api";
import { useApi, useConstants, useCookie } from "@/composables/useAppGlobals";
import { isTaskSaveConflictStatus } from "@/utils/taskAccess";

export function useRBC(
  persistenceScope: Readonly<Ref<string | null>> | undefined,
  snapshotVersion: Ref<number | null>,
  taskInstanceId: Ref<string | null>,
) {
  const $api = useApi();
  const $const = useConstants();
  const $cookie = useCookie();

  const isBusy = ref(false);
  const isReviewed = ref(false);
  const hasSaveConflict = ref(false);

  const configStore = useConfigStore();
  const slideStore = useRBCSlideStore();

  const { slideInfo } = storeToRefs(configStore);
  const currentTaskId = () => {
    const value = slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID];
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
  };
  const canPersist = computed(
    () =>
      !hasSaveConflict.value &&
      !configStore.isReadOnly &&
      Boolean(currentTaskId()) &&
      (persistenceScope === undefined || Boolean(persistenceScope.value)) &&
      typeof snapshotVersion.value === "number" &&
      Boolean(taskInstanceId.value),
  );

  function persistenceOrigin() {
    const taskId = currentTaskId();
    const scope = persistenceScope?.value ?? taskId;
    const version = snapshotVersion.value;
    const instanceId = taskInstanceId.value;
    if (!taskId || !scope || version === null || version === undefined || !instanceId) {
      throw new Error("Task annotation data is not loaded.");
    }
    return { taskId, scope, version, instanceId };
  }

  const cellBackendRequestObj = computed<RBCBackendPayload>(() => ({
    cells: slideStore.serializeSchema(),
  }));

  async function postCells(payload: ReportReviewPostPayload): Promise<boolean> {
    if (isBusy.value || !canPersist.value) return false;
    const origin = persistenceOrigin();
    const reviewer = $cookie.getCookie($const.COOKIES.KEY.USER_NAME);
    isBusy.value = true;
    try {
      const resp = await $api.postCells(origin.taskId, {
        ...payload,
        task_instance_id: origin.instanceId,
        base_snapshot_version: origin.version,
      });
      if (
        currentTaskId() !== origin.taskId ||
        (persistenceScope?.value ?? origin.taskId) !== origin.scope
      )
        return false;
      const nextVersion = resp.data.snapshot_version;
      if (typeof nextVersion === "number") {
        snapshotVersion.value = nextVersion;
      }
      configStore.updateSlideInfoKey(
        $const.SLIDE_KEYS.REVIEW_TIME,
        resp.data[$const.SLIDE_KEYS.REVIEW_TIME],
      );
      configStore.updateSlideInfoKey($const.SLIDE_KEYS.REVIEWER, reviewer);
      isReviewed.value = true;
      return true;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (!isTaskSaveConflictStatus(status)) throw error;
      if (
        currentTaskId() === origin.taskId &&
        (persistenceScope?.value ?? origin.taskId) === origin.scope
      ) {
        snapshotVersion.value = null;
        taskInstanceId.value = null;
        hasSaveConflict.value = true;
      }
      return false;
    } finally {
      isBusy.value = false;
    }
  }

  async function save(): Promise<boolean> {
    if (!canPersist.value) return false;
    const payload: ReportReviewPostPayload = {
      ...cellBackendRequestObj.value,
      state: (slideInfo.value[$const.SLIDE_KEYS.STATE] as string) || "",
    };
    return postCells(payload);
  }

  function resetState() {
    slideStore.reset();
    configStore.resetState();
  }

  function clearSaveConflict() {
    hasSaveConflict.value = false;
  }

  return {
    isBusy,
    isReviewed,
    hasSaveConflict,
    canPersist,
    slideInfo,
    cellBackendRequestObj,
    postCells,
    save,
    clearSaveConflict,
    resetState,
  };
}

export default useRBC;
